import type { Application } from '@data/hooks/api/use-applications-api/use-applications-api';

/**
 * Formats a grade string by stripping leading zeros.
 * e.g. "01" → "1", "00" → "0"
 */
export function formatGrade(grade: string) {
  return grade.replace(/^0+/, '') || '0';
}

/**
 * Formats a grade range for display.
 * Returns a single grade when low === high, or a "low-high" range otherwise.
 * e.g. ("03", "03") → "3", ("01", "05") → "1-5"
 */
export function formatGradeRange(gradeLow: string, gradeHigh: string): string {
  return gradeLow === gradeHigh
    ? formatGrade(gradeLow)
    : `${formatGrade(gradeLow)}-${formatGrade(gradeHigh)}`;
}

/**
 * Derives the widest grade range across all products in an application
 * and returns a formatted display string (e.g. "2-5" or "3").
 * Returns undefined when no grade info is available.
 */
export function getGradeDisplay(application: Application): string | undefined {
  const products = application.products ?? [];
  const lows = products.map((p) => p.gradeLow).filter(Boolean) as string[];
  const highs = products.map((p) => p.gradeHigh).filter(Boolean) as string[];

  const gradeLow = lows.length > 0 ? lows[0] : undefined;
  const gradeHigh = highs.length > 0 ? highs[highs.length - 1] : undefined;

  return gradeLow && gradeHigh
    ? formatGradeRange(gradeLow, gradeHigh)
    : undefined;
}
